"use client";

import { useState, useCallback } from "react";
import { Loader2, Building2, User, MapPin, FileText, ExternalLink, CheckCircle } from "lucide-react";
import { routes } from "@/lib/routes";
interface BillingAddressFormProps {
  role: "seller" | "buyer";
  initialValues?: {
    company?: string | null;
    name?: string | null;
    addressLine1?: string | null;
    addressLine2?: string | null;
    postalCode?: string | null;
    city?: string | null;
    country?: string | null;
    taxId?: string | null;
  };
  onSubmit: (values: {
    company?: string;
    name: string;
    addressLine1: string;
    addressLine2?: string;
    postalCode: string;
    city: string;
    country: string;
    taxId?: string;
    acceptTerms?: boolean;
  }) => Promise<void>;
  disabled?: boolean;
  className?: string;
}

/**
 * BillingAddressForm - Form for entering billing address + accepting terms in one step
 */
export function BillingAddressForm({
  role,
  initialValues,
  onSubmit,
  disabled = false,
  className = ""
}: BillingAddressFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [company, setCompany] = useState(initialValues?.company ?? "");
  const [name, setName] = useState(initialValues?.name ?? "");
  const [addressLine1, setAddressLine1] = useState(initialValues?.addressLine1 ?? "");
  const [addressLine2, setAddressLine2] = useState(initialValues?.addressLine2 ?? "");
  const [postalCode, setPostalCode] = useState(initialValues?.postalCode ?? "");
  const [city, setCity] = useState(initialValues?.city ?? "");
  const [country] = useState(initialValues?.country ?? "DE");
  const [taxId, setTaxId] = useState(initialValues?.taxId ?? "");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const validate = useCallback(() => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) {
      newErrors.name = "Name ist erforderlich";
    }
    if (!addressLine1.trim()) {
      newErrors.addressLine1 = "Adresse ist erforderlich";
    }
    if (!postalCode.trim() || postalCode.length < 4) {
      newErrors.postalCode = "Gültige PLZ erforderlich";
    }
    if (!city.trim()) {
      newErrors.city = "Stadt ist erforderlich";
    }
    if (!termsAccepted) {
      newErrors.terms = "Bitte akzeptieren Sie die Vertragsbedingungen";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [name, addressLine1, postalCode, city, termsAccepted]);
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      await onSubmit({
        company: company.trim() || undefined,
        name: name.trim(),
        addressLine1: addressLine1.trim(),
        addressLine2: addressLine2.trim() || undefined,
        postalCode: postalCode.trim(),
        city: city.trim(),
        country,
        taxId: taxId.trim() || undefined,
        acceptTerms: true
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [validate, onSubmit, company, name, addressLine1, addressLine2, postalCode, city, country, taxId]);
  const roleLabel = role === "seller" ? "Verkäufer" : "Käufer";
  return <form onSubmit={handleSubmit} className={`space-y-4 ${className}`}>
      <div className="flex items-center gap-2 mb-4">
        <FileText className="w-5 h-5 text-zinc-400" />
        <h3 className="font-medium text-zinc-900">Rechnungsadresse {roleLabel}</h3>
      </div>

      {/* Company (optional) */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-zinc-700 mb-1">
          <Building2 className="w-4 h-4" />
          Firma (optional)
        </label>
        <input type="text" value={company} onChange={e_0 => setCompany(e_0.target.value)} disabled={disabled || isSubmitting} className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-zinc-100" placeholder="GmbH, AG, etc." />
      </div>

      {/* Name */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-zinc-700 mb-1">
          <User className="w-4 h-4" />
          Name *
        </label>
        <input type="text" value={name} onChange={e_1 => setName(e_1.target.value)} disabled={disabled || isSubmitting} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-zinc-100 ${errors.name ? "border-red-300" : "border-zinc-300"}`} placeholder="Max Mustermann" />
        {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
      </div>

      {/* Address Line 1 */}
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-zinc-700 mb-1">
          <MapPin className="w-4 h-4" />
          Adresse *
        </label>
        <input type="text" value={addressLine1} onChange={e_2 => setAddressLine1(e_2.target.value)} disabled={disabled || isSubmitting} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-zinc-100 ${errors.addressLine1 ? "border-red-300" : "border-zinc-300"}`} placeholder="Musterstraße 123" />
        {errors.addressLine1 && <p className="mt-1 text-sm text-red-600">{errors.addressLine1}</p>}
      </div>

      {/* Address Line 2 (optional) */}
      <div>
        <label className="text-sm font-medium text-zinc-700 mb-1 block">
          Adresszusatz (optional)
        </label>
        <input type="text" value={addressLine2} onChange={e_3 => setAddressLine2(e_3.target.value)} disabled={disabled || isSubmitting} className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-zinc-100" placeholder="Gebäude B, 3. Stock" />
      </div>

      {/* PLZ and City */}
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="text-sm font-medium text-zinc-700 mb-1 block">PLZ *</label>
          <input type="text" value={postalCode} onChange={e_4 => setPostalCode(e_4.target.value)} disabled={disabled || isSubmitting} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-zinc-100 ${errors.postalCode ? "border-red-300" : "border-zinc-300"}`} placeholder="12345" />
          {errors.postalCode && <p className="mt-1 text-sm text-red-600">{errors.postalCode}</p>}
        </div>
        <div className="col-span-2">
          <label className="text-sm font-medium text-zinc-700 mb-1 block">Stadt *</label>
          <input type="text" value={city} onChange={e_5 => setCity(e_5.target.value)} disabled={disabled || isSubmitting} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-zinc-100 ${errors.city ? "border-red-300" : "border-zinc-300"}`} placeholder="Berlin" />
          {errors.city && <p className="mt-1 text-sm text-red-600">{errors.city}</p>}
        </div>
      </div>

      {/* Country (fixed to Germany for now) */}
      <div>
        <label className="text-sm font-medium text-zinc-700 mb-1 block">Land</label>
        <input type="text" value="Deutschland" disabled className="w-full px-3 py-2 border border-zinc-300 rounded-lg bg-zinc-100 text-zinc-500" />
      </div>

      {/* Tax ID - sellers only */}
      {role === "seller" && <div>
          <label className="text-sm font-medium text-zinc-700 mb-1 block">
            Steuernummer (optional)
          </label>
          <input type="text" value={taxId} onChange={e_6 => setTaxId(e_6.target.value)} disabled={disabled || isSubmitting} className="w-full px-3 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary disabled:bg-zinc-100" placeholder="12345678901" />
        </div>}

      {/* Terms acceptance */}
      <div className="border-t border-zinc-200 pt-4 mt-4">
        <p className="text-sm font-medium text-zinc-700 mb-3">Vertragsbedingungen</p>
        <div className="bg-zinc-50 rounded-lg p-3 mb-3 text-sm text-zinc-600 space-y-1.5">
          <p>Durch das Absenden bestätigen Sie:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Die Angaben im Vertrag sind korrekt und vollständig</li>
            <li>Die Rechnungsadresse ist aktuell und zutreffend</li>
            <li>Sie sind berechtigt, diesen Vertrag abzuschließen</li>
          </ul>
          <a href={routes.content.agb} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary hover:text-primary/80 mt-1">
            AGB und Datenschutzbestimmungen lesen <ExternalLink className="w-3 h-3" />
          </a>
        </div>

        <label className={`flex items-start gap-3 cursor-pointer ${errors.terms ? "text-red-600" : ""}`}>
          <input type="checkbox" checked={termsAccepted} onChange={e_7 => setTermsAccepted(e_7.target.checked)} disabled={disabled || isSubmitting} className="mt-1 w-5 h-5 rounded border-zinc-300 text-primary focus:ring-primary disabled:opacity-50" />
          <span className="text-sm text-zinc-700">
            Ich habe die Vertragsbedingungen gelesen und akzeptiere diese.
          </span>
        </label>
        {errors.terms && <p className="mt-1 text-sm text-red-600">{errors.terms}</p>}
      </div>

      {/* Submit button */}
      <button type="submit" disabled={disabled || isSubmitting} className="w-full px-4 py-2 bg-primary text-white font-medium rounded-lg hover:bg-primary/90 disabled:bg-zinc-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2">
        {isSubmitting ? <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Wird gespeichert...
          </> : <>
            <CheckCircle className="w-4 h-4" />
            Bestätigen und absenden
          </>}
      </button>
    </form>;
}